import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FileText, Plus } from "lucide-react";
import api from "../../api";
import useFeatures from "../../hooks/useFeatures";
import { jwtDecode } from "jwt-decode";

// Settings → Clinical & Safety → Note templates. Lists the templates in use
// (built-in SOAP/DAP/BIRP plus the clinic's own) and lets a clinician or admin
// make one. Saved through the clinical content store: published straight away
// in a clinic without supervisors, otherwise sent to a supervisor to approve.
const STATUS = { draft: "Draft", pending_review: "Waiting for supervisor", approved: "In use", rejected: "Changes requested", retired: "Replaced" };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 30);

export default function NoteTemplatesCard() {
  const features = useFeatures();
  const [live, setLive] = useState([]);
  const [own, setOwn] = useState([]);
  const [form, setForm] = useState(null); // { name, sections: "Label | hint\n..." }
  const [saving, setSaving] = useState(false);
  const [defaultCode, setDefaultCode] = useState("");
  let isOrgAdmin = false;
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    isOrgAdmin = (jwtDecode(auth.token)?.sub?.role || "").toLowerCase() === "admin";
  } catch { isOrgAdmin = false; }

  const load = () => {
    api.getNoteTemplates().then((r) => setLive(r.data || [])).catch(() => {});
    api.getGeneralSettings().then((r) => setDefaultCode(r.data?.default_note_template || "")).catch(() => {});
    api.getClinicalContentManage("note_template").then((r) => setOwn(r.data?.items || [])).catch(() => setOwn([]));
  };
  useEffect(() => { if (features.has("note_templates")) load(); }, [features.ready]); // eslint-disable-line

  if (!features.has("note_templates")) return null;

  const saveDefault = async (code) => {
    const before = defaultCode;
    setDefaultCode(code);
    try {
      await api.updateGeneralSettings({ default_note_template: code });
      toast.success(code ? "Default note format saved" : "No default: clinicians choose a format for each session");
    } catch (e) {
      setDefaultCode(before);
      toast.error(e?.response?.data?.error || "Couldn't save the default format.");
    }
  };

  const save = async () => {
    const sections = form.sections.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [label, ...hint] = l.split("|");
      return { key: slug(label) || `section_${Math.random().toString(36).slice(2, 7)}`, label: label.trim(), hint: hint.join("|").trim() };
    });
    if (!form.name.trim() || sections.length === 0) { toast.error("Give the template a name and at least one section."); return; }
    if (new Set(sections.map((s) => s.key)).size !== sections.length) { toast.error("Each section needs a different name."); return; }
    setSaving(true);
    try {
      const code = `custom_${slug(form.name)}`;
      const created = await api.createClinicalContent({ kind: "note_template", code, title: form.name.trim(), body: { name: form.name.trim(), sections } });
      const submitted = await api.submitClinicalContent(created.data.id);
      toast.success(submitted.data?.status === "approved" ? "Template published - clinicians can pick it now" : "Sent to a supervisor to approve");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the template.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800"><FileText className="w-5 h-5 text-primary-600" /> Note templates</h3>
        {!form && <button type="button" onClick={() => setForm({ name: "", sections: "" })} className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-1.5"><Plus className="w-4 h-4" /> New template</button>}
      </div>
      <p className="text-xs md:text-sm text-gray-500">New session notes start in the default format below; a clinician can still change it on the consultation's Clinical note tab. The drafts made from a session's transcript follow the same format.</p>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="default_note_template" className="normal-case font-sans tracking-normal text-sm font-semibold text-gray-700">Default format for new notes</label>
        <select id="default_note_template" value={defaultCode} disabled={!isOrgAdmin} onChange={(e) => saveDefault(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm">
          <option value="">Not set - ask the clinician each session</option>
          {live.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
        </select>
        {!isOrgAdmin && <span className="text-xs text-gray-500">Only your clinic's admin can change this.</span>}
      </div>
      <ul className="flex flex-wrap gap-2">
        {live.map((t) => (
          <li key={t.code} className="rounded-full bg-white border border-gray-200 px-3 py-1 text-xs text-gray-700">
            {t.name}{t.source === "org" ? " · your clinic" : ""}
          </li>
        ))}
      </ul>
      {own.filter((i) => i.status !== "approved" && i.status !== "retired").length > 0 && (
        <ul className="text-xs text-gray-600 space-y-1">
          {own.filter((i) => i.status !== "approved" && i.status !== "retired").map((i) => (
            <li key={i.id}>{i.title} - <span className="font-semibold">{STATUS[i.status] || i.status}</span>{i.review_note ? ` ("${i.review_note}")` : ""}</li>
          ))}
        </ul>
      )}
      {form && (
        <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Template name, e.g. Couples session note"
            aria-label="Template name" className="w-full rounded-md border border-gray-300 p-2 text-sm" />
          <textarea value={form.sections} onChange={(e) => setForm({ ...form, sections: e.target.value })} rows={6}
            aria-label="Sections"
            placeholder={"One section per line. Add a hint after a | if you like:\nPresenting concerns | What each partner raised\nPatterns observed\nInterventions\nHomework | What each will try before next time"}
            className="w-full rounded-md border border-gray-300 p-2 text-sm" />
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save template"}</button>
            <button type="button" onClick={() => setForm(null)} className="rounded-lg border border-gray-300 text-sm px-4 py-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
